/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemRole, TableInfoSpec, TriggerSpec } from "../../../shared/contracts";
import { TriggerShape } from "../../core/enums";
import { ItemData, toDragPoints, toVertex2D } from "../../core/item-data";
import type { DragPoint } from "../../math/dragpoint";
import type { Vertex2D } from "../../math/vertex2d";

export class TriggerData extends ItemData {
  public dragPoints: DragPoint[] = [];
  public center: Vertex2D;
  public radius: number = 25;
  public rotation: number = 0;
  public szMaterial?: string;
  public szSurface?: string;
  public isVisible: boolean = true;
  public isEnabled: boolean = true;
  public hitHeight: number = 50;
  public shape: number = TriggerShape.TriggerWireA;
  public animSpeed: number = 1;
  public wireThickness: number = 0;
  public role: ItemRole = "none";

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: TriggerSpec, _info?: TableInfoSpec): TriggerData {
    const data = new TriggerData(spec.name, toVertex2D(spec.center));
    data.dragPoints = toDragPoints(spec.dragPoints);
    data.radius = spec.radius ?? data.radius;
    data.rotation = spec.rotation ?? data.rotation;
    data.shape = spec.shape ?? data.shape;
    data.hitHeight = spec.hitHeight ?? data.hitHeight;
    data.wireThickness = spec.wireThickness ?? data.wireThickness;
    data.szMaterial = spec.szMaterial;
    data.szSurface = spec.szSurface;
    data.isVisible = spec.isVisible ?? true;
    data.isEnabled = spec.isEnabled ?? true;
    data.role = spec.role ?? "none";
    return data;
  }
}
